import { groupBy, mapAsync, minBy, partition } from 'es-toolkit';

import { getUserVisibleOrDefaultExceptionMessage, ReadFile, toUploadFile } from '@paragon/client-tools';
import {
  ComponentDesign,
  ComponentDesignTimestamps,
  ComponentDesignUploadFile,
  ProtoJackGirderConnection,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { Point2D } from '@paragon/geometry-library';
import { hasAssemblyPermission, hasNewLayoutModeFeatureFlag } from '@paragon/login-tools';
import { enqueueError, enqueueSuccess, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { calculateBearingGeometry } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { hasInvalidRelativeGeometry } from '../../library/relativeGeometry/hasInvalidRelativeGeometry';
import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { getOrganizationForOpenContext } from '../selectors/getOrganizationForInsertingComponent';
import { getProjectLayoutModel } from '../selectors/getProjectLayoutModel';
import { isInPreviewMode } from '../selectors/isInPreviewModeSelector';
import { isParagonJobOpen } from '../selectors/jobContextSelectors';
import { isLongRunningUploadOngoing } from '../selectors/longRunningOperationSelectors';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { setJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import {
  concludeLongRunningUpload,
  finalizeLongRunningUpload,
  finishUploadingComponentDesignInLongRunningUpload,
  startLongRunningUpload,
  startUploadingComponentDesignInLongRunningUpload,
} from '../slices/longRunningOperationsSlice';
import { addComponentDesignsToProject, addNewComponentDesignsToJobLayoutModel, sendCanaryReport } from './appActions';
import { extractBearingEnvelopesFromImage } from './bearingExtractionActions';
import {
  loadLayoutData,
  loadStandardDimensions,
  MAX_CONCURRENT_COMPONENT_DESIGN_LOADS,
  receiveComponentDesign,
} from './loadActions';
import { cancelActiveAnalysisResultRequests } from './loadAnalysisResults';
import { analyzeComponentDesign } from './paragonProcessActions';
import { selectItem } from './selectActions';
import { addComponentDesignsToActiveStation } from './stationActions';
import { AsyncThunkActionVoid } from './thunkActions';

const originalAnalysisName = 'Original Analysis';

const MAX_UPLOAD_TASKS = 10;

const BEFORE_UPLOAD_MESSAGE = 'Reading selected files...';

export function beforeUploadFiles() {
  return addSpinnerMessage(BEFORE_UPLOAD_MESSAGE);
}

export const uploadFiles =
  (
    files: ReadFile[],
    options: { persist: boolean; analyze: boolean; addToStation: boolean; beforeUploadSpinnerId: number | null },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const [countFiles, componentFiles] = partition(files, (file) => file.filetype === 'cnt' || file.filetype === 'seq');

    if (options.addToStation) {
      if (countFiles.length > 1) {
        dispatch(enqueueError('You can upload at most one count file for a station.'));
        return;
      }

      if (countFiles.length > 0 && componentFiles.length === 0) {
        dispatch(enqueueError('You must upload at least one component file (in addition to the count file).'));
        return;
      }
    }

    if (options.beforeUploadSpinnerId != null) {
      dispatch(removeSpinnerMessage(options.beforeUploadSpinnerId));
    }

    const spinnerId = dispatch(addSpinnerMessage(`${options.persist ? 'Upload' : 'Open'}ing files...`));

    try {
      const {
        application: { openContext },
      } = getState();

      if (files.length === 1 && ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(files[0].filetype)) {
        dispatch(extractBearingEnvelopesFromImage(files[0]));
        return;
      }

      if (files.length === 1 && files[0].filetype === 'ifc') {
        if (!hasNewLayoutModeFeatureFlag(getState()))
          throw { userVisibleMessage: 'You do not have permission to to upload an IFC file.' };
        if (openContext?.type !== 'project')
          throw { userVisibleMessage: 'You must be in a project to upload an IFC file.' };

        await designService.componentDesigns.postUploadIfcFile(files[0], openContext.projectGuid);
        await dispatch(loadLayoutData(openContext.projectGuid));

        const roofContainer = Object.values(getState().layout.roofContainers)[0];

        dispatch(selectItem(roofContainer, 'roofContainer'));
        focusCameraOnSelectedObjects();

        return;
      }

      const organization = getOrganizationForOpenContext(getState());
      const projectLayoutModel = getProjectLayoutModel(getState());

      const materialSelection = projectLayoutModel?.materialSelection ?? null;

      let componentDesigns: ReadonlyArray<ComponentDesign> = [];
      let errors: readonly string[] = [];
      let warnings: readonly string[] = [];
      let timestamps: ReadonlyArray<ComponentDesignTimestamps> = [];
      let protoJackGirderConnections: readonly ProtoJackGirderConnection[] = [];
      let quantitiesFromFiles: { [componentDesignGuid: string]: number } | undefined;

      if (options.persist) {
        cancelActiveAnalysisResultRequests();

        if (
          (files.some((file) => file.filetype === 'tps') && files.some((file) => file.filetype === 'of')) ||
          (files.some((file) => file.filetype === 'trs') && files.some((file) => file.filetype === 'ans')) ||
          files.some((file) => file.filetype === 'zip')
        ) {
          // The above blacklist catches cases for which we can't show the upload dialog -- .zip files need to be
          //   extracted, and .tps+.of or .trs+.ans file combinations can't be predicted client-side because they
          //   require reading the file contents.  So if this blacklist is triggered, we skip the upload dialog and
          //   instead upload all the files together and let the server figure it out.

          const filesForUpload = files.map(toUploadFile);
          const uploadFilesResult = await designService.componentDesigns.postUploadFiles(
            filesForUpload,
            organization,
            materialSelection,
            openContext?.type === 'project' ? openContext.projectGuid : undefined,
          );
          componentDesigns = uploadFilesResult.componentDesignsWithTimestamps.map(
            (componentDesignWithTimestamps) => componentDesignWithTimestamps.componentDesign,
          );
          errors = uploadFilesResult.errors;
          warnings = uploadFilesResult.warnings;
          quantitiesFromFiles = uploadFilesResult.quantitiesFromFiles;
          timestamps = uploadFilesResult.componentDesignsWithTimestamps.map(
            (componentDesignWithTimestamps) => componentDesignWithTimestamps.timestamps,
          );
          protoJackGirderConnections = uploadFilesResult.protoJackGirderConnections;
        } else {
          const componentsToUpload: { name: string; files: ComponentDesignUploadFile[] }[] = [];

          let files: ReadFile[];
          for (files of Object.values(groupBy(componentFiles, ({ filename }) => filename.toLowerCase()))) {
            const ansFiles = files.filter((file) => file.filetype === 'ans');
            const treFiles = files.filter((file) => file.filetype === 'tre');
            const pdfFiles = files.filter((file) => file.filetype === 'pdf');

            // If there is 1 TRE and 1 ANS file with the same name, group them together, and include any PDF files with
            // the same name so they are not uploaded as separate components.
            if (ansFiles.length === 1 && treFiles.length === 1) {
              componentsToUpload.push({
                name: treFiles[0].filename.toLowerCase(),
                files: [treFiles[0], ansFiles[0], ...pdfFiles].map(toUploadFile),
              });

              files = files.filter((file) => file !== ansFiles[0] && file !== treFiles[0] && !pdfFiles.includes(file));
            }

            // A pair of .ans + .tre files and any .pdf files of the same name is the only valid grouping that we
            //   currently handle.  All other groupings of component files are either not supported or will have
            //   triggered the blacklist above and prevented us from reaching this point.  So even though these files
            //   have the same name, we need to upload them as separate components.
            for (const file of files) {
              componentsToUpload.push({ name: `${file.filename}.${file.filetype}`, files: [toUploadFile(file)] });
            }
          }

          dispatch(
            startLongRunningUpload({ componentDesignNames: componentsToUpload.map((component) => component.name) }),
          );

          const uploadTasks: Promise<void>[] = [];
          const componentStack = componentsToUpload.toReversed();

          const runUploadTask = async () => {
            while (componentStack.length > 0) {
              const nextComponent = componentStack.pop()!;
              dispatch(startUploadingComponentDesignInLongRunningUpload({ name: nextComponent.name }));

              try {
                const uploadFilesResult = await designService.componentDesigns.postUploadFiles(
                  nextComponent.files,
                  organization,
                  materialSelection,
                  openContext?.type === 'project' ? openContext.projectGuid : undefined,
                );

                componentDesigns = componentDesigns.concat(
                  uploadFilesResult.componentDesignsWithTimestamps.map(
                    (componentDesignWithTimestamps) => componentDesignWithTimestamps.componentDesign,
                  ),
                );
                errors = errors.concat(uploadFilesResult.errors);
                warnings = warnings.concat(uploadFilesResult.warnings);
                quantitiesFromFiles = quantitiesFromFiles
                  ? { ...quantitiesFromFiles, ...uploadFilesResult.quantitiesFromFiles }
                  : uploadFilesResult.quantitiesFromFiles;
                timestamps = timestamps.concat(
                  uploadFilesResult.componentDesignsWithTimestamps.map(
                    (componentDesignWithTimestamps) => componentDesignWithTimestamps.timestamps,
                  ),
                );
                protoJackGirderConnections = protoJackGirderConnections.concat(
                  uploadFilesResult.protoJackGirderConnections,
                );

                dispatch(
                  finishUploadingComponentDesignInLongRunningUpload({
                    name: nextComponent.name,
                    errors: [...uploadFilesResult.errors],
                    warnings: [...uploadFilesResult.warnings],
                  }),
                );
              } catch (error) {
                console.error(error);

                const errorMessages = [getUserVisibleOrDefaultExceptionMessage(error, 'Unknown error.')];
                dispatch(
                  finishUploadingComponentDesignInLongRunningUpload({
                    name: nextComponent.name,
                    errors: errorMessages,
                    warnings: [],
                  }),
                );
              }
            }
          };

          for (let i = 0; i < MAX_UPLOAD_TASKS; i++) {
            uploadTasks.push(runUploadTask());
          }

          await Promise.all(uploadTasks);
        }
      } else {
        const filesForUpload = componentFiles.map(toUploadFile);
        const parseFilesResult = await designService.componentDesigns.postParseFiles(
          filesForUpload,
          organization,
          materialSelection,
        );
        componentDesigns = parseFilesResult.parsedComponentDesigns.map(
          (parsedComponentDesign) => parsedComponentDesign.componentDesign,
        );
        errors = parseFilesResult.errors;
      }

      if (!isLongRunningUploadOngoing(getState())) {
        for (const error of errors) {
          dispatch(enqueueError(error));
        }
        for (const warning of warnings) {
          dispatch(enqueueWarning(warning));
        }
      }

      const [designsWithJacks, designsWithoutJacks] = partition(componentDesigns, (c) =>
        protoJackGirderConnections.some((j) => j.girderGuid === c.guid),
      );

      const jackGirderConnections = protoJackGirderConnections
        .map((protoJackGirderConnection) => ({
          protoJackGirderConnection,
          jack: componentDesigns.find(
            (c) => c.component.name.toLowerCase() === protoJackGirderConnection.jackName.toLowerCase(),
          ),
        }))
        .map(({ protoJackGirderConnection, jack }) => {
          if (jack == null) {
            return null;
          }

          return {
            guid: crypto.randomUUID(),
            locked: false,
            userCreated: true,
            jackGuid: jack.guid,
            girderGuid: protoJackGirderConnection.girderGuid,
            jackBearingGuid: minBy(jack.component.bearings, (b) =>
              calculateBearingGeometry(b, jack.component)!.position.distanceTo(
                new Point2D(protoJackGirderConnection.bearingLocationX, protoJackGirderConnection.bearingLocationY),
              ),
            )!.guid,
            memberType: protoJackGirderConnection.memberType,
            chordToUseWhenMultipleChoices: protoJackGirderConnection.chordToUseWhenMultipleChoices,
            xLocation: protoJackGirderConnection.xLocation,
            minimumAngleBetweenGirderAndJack: protoJackGirderConnection.minimumAngleBetweenGirderAndJack,
            skewFromLeft: null,
            girderFace: null,
            hangerGuid: null,
            hangerCarriedItemConfigurationGuid: null,
            numberOfJacks: protoJackGirderConnection.numberOfJacks,
            spacingOfJacks: protoJackGirderConnection.spacingOfJacks,
          };
        })
        .filter(notNullish);

      componentDesigns = designsWithJacks.concat(designsWithoutJacks);

      await mapAsync(
        componentDesigns,
        (componentDesign) =>
          dispatch(
            receiveComponentDesign(componentDesign, {
              commitComponentDesign: true,
              replaceWorkingChanges: true,
              isPersisted: options.persist,
              skipJackGirderConnections: false,
            }),
          ),
        { concurrency: MAX_CONCURRENT_COMPONENT_DESIGN_LOADS },
      );

      for (const componentDesignTimestamps of timestamps) {
        dispatch(setComponentDesignTimestamps({ timestamps: componentDesignTimestamps }));
      }

      if (!options.persist) {
        await mapAsync(componentDesigns, (componentDesign) => dispatch(loadStandardDimensions(componentDesign)), {
          concurrency: MAX_CONCURRENT_COMPONENT_DESIGN_LOADS,
        });
      }

      const firstComponentDesign = componentDesigns[0];
      const state = getState();
      if (!isInPreviewMode(state)) {
        if (firstComponentDesign) dispatch(selectItem(firstComponentDesign, 'componentDesign'));
        focusCameraOnSelectedObjects();
      }

      if (state.application.openContext?.type === 'project') {
        const parentGuid = state.application.openContext.projectGuid;

        await dispatch(
          addComponentDesignsToProject(
            componentDesigns.map((c) => c.guid),
            parentGuid,
            quantitiesFromFiles,
          ),
        );
      } else if (state.application.openContext?.type === 'station' && options.addToStation) {
        await dispatch(
          addComponentDesignsToActiveStation(
            componentDesigns.map((c) => c.guid),
            countFiles[0],
            quantitiesFromFiles,
          ),
        );
      } else if (isParagonJobOpen(state)) {
        dispatch(addNewComponentDesignsToJobLayoutModel(componentDesigns.map((c) => c.guid)));
      }

      await designService.jackGirderConnections.insert(jackGirderConnections);
      dispatch(setJackGirderConnections(jackGirderConnections));

      // Assembly users are not subject to the invalid relative geometry error notifications, but we are still
      // interested in when they supply trusses with invalid relative geometry.
      if (hasAssemblyPermission(state)) {
        for (const componentDesign of componentDesigns) {
          const invalidRelativeGeometry = hasInvalidRelativeGeometry(componentDesign.component);
          if (invalidRelativeGeometry) {
            dispatch(
              sendCanaryReport('Assembly user encountered invalid relative geometry', {
                componentDesign: JSON.stringify(componentDesign),
              }),
            );
          }
        }
      }

      if (options.analyze) {
        await Promise.all(
          componentDesigns.map((componentDesign, index) => {
            if (componentDesign) {
              try {
                return dispatch(
                  analyzeComponentDesign(componentDesign, {
                    commitName: originalAnalysisName,
                    selectWhenFinished: index === 0,
                    addSpinner: false,
                  }),
                );
              } catch (exception) {
                // Individual components can fail analysis without failing the entire upload
                console.error(exception);
              }
            }
            return Promise.resolve();
          }),
        );
      }
    } catch (exception) {
      console.error(exception);
      const exceptionMessage = getUserVisibleOrDefaultExceptionMessage(exception, 'Error while uploading files.');
      dispatch(enqueueError(exceptionMessage));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));

      if (isLongRunningUploadOngoing(getState())) {
        dispatch(finalizeLongRunningUpload());
        const longRunningUploadOperation = getState().longRunningOperations.upload;

        // If all uploads were successful, trigger closing the dialog so the user doesn't have to manually close it
        if (
          longRunningUploadOperation &&
          Object.keys(longRunningUploadOperation.errors).length === 0 &&
          Object.keys(longRunningUploadOperation.warnings).length === 0
        ) {
          dispatch(concludeLongRunningUpload());
          dispatch(enqueueSuccess('All components uploaded successfully.'));
        }
      }
    }
  };
